import React, { useState, useEffect } from 'react';
import { Box, Typography, alpha, Skeleton, Chip, Select, MenuItem } from '@mui/material';
import { History } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { portfolioAPI } from '../../services/api';

const fmtDate = (dateStr) => {
  try {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch(e) { return dateStr?.slice(5) || ''; }
};

const HistoryPerformance = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await portfolioAPI.getWatchlistHistoryPerformance(days);
        setData(res.data);
      } catch (err) {
        console.error('Failed to load history:', err);
        setError(t('regimeWatchlist.history.loadError', 'Failed to load history data'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [days, t]);

  if (loading) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Skeleton variant="rounded" height={60} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (error) return (
    <Box sx={{ p: 1.5, textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  if (!data?.history?.length) return null;

  const summary = data.summary;

  return (
    <Box>
      {/* Summary bar */}
      {summary && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1,
          bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb',
          flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <History sx={{ fontSize: 15, color: '#64748b' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#334155' }}>
              {t('regimeWatchlist.history.title', 'Historical Performance')}
            </Typography>
          </Box>
          <Chip
            label={`${summary.checked_dates} ${t('regimeWatchlist.history.sessions', 'sessions')}`}
            size="small"
            sx={{ height: 20, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }}
          />
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            size="small"
            sx={{ height: 22, fontSize: '0.6rem', fontWeight: 700, borderRadius: 1, '& .MuiSelect-select': { py: 0.2, px: 0.8 } }}
          >
            {[30, 60, 90, 180].map(d => (
              <MenuItem key={d} value={d} sx={{ fontSize: '0.65rem' }}>{d} {t('regimeWatchlist.history.days', 'days')}</MenuItem>
            ))}
          </Select>
          <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ret10d TB</Typography>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 900, fontFamily: 'monospace',
                color: summary.overall_avg_ret10d > 0 ? '#10b981' : '#ef4444',
              }}>
                {summary.overall_avg_ret10d > 0 ? '+' : ''}{summary.overall_avg_ret10d}%
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>WR TB</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, fontFamily: 'monospace', color: '#3b82f6' }}>
                {summary.overall_avg_wr}%
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{t('regimeWatchlist.history.consistency', 'Consistency')}</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, fontFamily: 'monospace', color: '#8b5cf6' }}>
                {summary.consistency}%
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* History rows */}
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '80px 70px 50px 65px 55px minmax(100px, 1fr) minmax(100px, 1fr)',
          gap: 0.5, alignItems: 'center',
          pb: 0.5, borderBottom: '1px solid #f1f5f9', mb: 0.5,
        }}>
          {[t('regimeWatchlist.history.date', 'DATE'), 'REGIME', t('regimeWatchlist.history.stockCount', 'STOCKS'), 'RET 10D', 'WR', 'TOP WINNERS', 'TOP LOSERS'].map(h => (
            <Typography key={h} sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {h}
            </Typography>
          ))}
        </Box>

        {data.history.map((row, i) => {
          const retColor = row.avg_ret_10d > 2 ? '#10b981' : row.avg_ret_10d > 0 ? '#3b82f6' : '#ef4444';
          return (
            <Box key={i} sx={{
              display: 'grid',
              gridTemplateColumns: '80px 70px 50px 65px 55px minmax(100px, 1fr) minmax(100px, 1fr)',
              gap: 0.5, alignItems: 'center',
              py: 0.4,
              borderBottom: '1px solid #f8fafc',
              '&:hover': { bgcolor: '#fafbfc' },
            }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', fontFamily: 'monospace' }}>
                {fmtDate(row.date)}
              </Typography>
              <Chip label={row.regime} size="small" sx={{
                height: 18, fontSize: '0.48rem', fontWeight: 700,
                bgcolor: alpha(retColor, 0.08), color: retColor,
              }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#334155', textAlign: 'center' }}>
                {row.count}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: retColor, fontFamily: 'monospace' }}>
                {row.avg_ret_10d > 0 ? '+' : ''}{row.avg_ret_10d}%
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', fontFamily: 'monospace' }}>
                {row.win_rate}%
              </Typography>
              {/* Winners */}
              <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                {(row.top_winners || []).slice(0, 3).map((w, j) => (
                  <Typography key={j} sx={{ fontSize: '0.55rem', color: '#10b981', fontWeight: 600 }}>
                    {w.symbol} +{w.ret_10d}%
                  </Typography>
                ))}
              </Box>
              {/* Losers */}
              <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                {(row.top_losers || []).slice(0, 3).map((w, j) => (
                  <Typography key={j} sx={{ fontSize: '0.55rem', color: '#ef4444', fontWeight: 600 }}>
                    {w.symbol} {w.ret_10d}%
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default HistoryPerformance;
